import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, ShieldOff, Plus, Loader2, AlertTriangle, Calendar, FileText } from 'lucide-react';
import { format } from 'date-fns';
import apiClient from '../../api/client';

interface LegalHoldManagerProps {
  caseNumber?: string;
  evidenceId?: string;
  onHoldApplied?: () => void;
  onHoldReleased?: () => void;
  className?: string;
}

export function LegalHoldManager({
  caseNumber,
  evidenceId,
  onHoldApplied,
  onHoldReleased,
  className = '',
}: LegalHoldManagerProps) {
  const queryClient = useQueryClient();
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [releaseHoldId, setReleaseHoldId] = useState<string | null>(null);
  const [releaseReason, setReleaseReason] = useState('');

  // Form state for applying hold
  const [holdName, setHoldName] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [orderReference, setOrderReference] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const { data: holds, isLoading } = useQuery({
    queryKey: ['legal-holds', caseNumber, evidenceId],
    queryFn: () => apiClient.getLegalHolds({ caseNumber, activeOnly: false }),
  });

  const applyHoldMutation = useMutation({
    mutationFn: async () => {
      if (!caseNumber) throw new Error('Case number required');
      return apiClient.applyLegalHold({
        caseNumber,
        evidenceId,
        holdName,
        holdReason,
        orderReference: orderReference || undefined,
        expiresAt: expiresAt || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-holds'] });
      queryClient.invalidateQueries({ queryKey: ['disposition'] });
      resetForm();
      onHoldApplied?.();
    },
  });

  const releaseHoldMutation = useMutation({
    mutationFn: async (holdId: string) => {
      return apiClient.releaseLegalHold(holdId, releaseReason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-holds'] });
      queryClient.invalidateQueries({ queryKey: ['disposition'] });
      setReleaseHoldId(null);
      setReleaseReason('');
      onHoldReleased?.();
    },
  });

  const resetForm = () => {
    setShowApplyForm(false);
    setHoldName('');
    setHoldReason('');
    setOrderReference('');
    setExpiresAt('');
  };

  const activeHolds = holds?.filter((h) => h.status === 'Active') || [];
  const pastHolds = holds?.filter((h) => h.status !== 'Active') || [];

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber-600" />
          <h3 className="font-medium text-gray-900">Legal Holds</h3>
          {activeHolds.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
              {activeHolds.length} active
            </span>
          )}
        </div>
        {!showApplyForm && caseNumber && (
          <button
            onClick={() => setShowApplyForm(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100"
          >
            <Plus className="h-4 w-4" />
            Apply Hold
          </button>
        )}
      </div>

      {/* Apply Hold Form */}
      {showApplyForm && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Apply Legal Hold</h4>
              <p className="text-sm text-amber-600 mt-1">
                This will prevent disposal and restrict modifications to the evidence.
              </p>
            </div>
          </div>

          <div className="grid gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hold Name *
              </label>
              <input
                type="text"
                value={holdName}
                onChange={(e) => setHoldName(e.target.value)}
                placeholder="e.g., Appeal Pending - Smith v. State"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Legal Justification *
              </label>
              <textarea
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                placeholder="Legal justification for the hold..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Court Order Reference
                </label>
                <input
                  type="text"
                  value={orderReference}
                  onChange={(e) => setOrderReference(e.target.value)}
                  placeholder="e.g., Order No. 2026-CV-1234"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires At (optional)
                </label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => applyHoldMutation.mutate()}
              disabled={applyHoldMutation.isPending || !holdName.trim() || !holdReason.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {applyHoldMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Shield className="h-4 w-4" />
              )}
              Apply Legal Hold
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {/* Active Holds */}
      {activeHolds.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-500">Active Holds</h4>
          {activeHolds.map((hold) => (
            <div
              key={hold.id}
              className="p-4 bg-amber-50 rounded-lg border border-amber-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{hold.holdName}</p>
                    <p className="text-sm text-gray-600 mt-1">{hold.holdReason}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Applied {format(new Date(hold.appliedAt), 'MMM d, yyyy')}
                      </span>
                      {hold.orderReference && (
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {hold.orderReference}
                        </span>
                      )}
                      {hold.expiresAt && (
                        <span>Expires {format(new Date(hold.expiresAt), 'MMM d, yyyy')}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {releaseHoldId === hold.id ? (
                  <div className="flex flex-col gap-2 ml-4">
                    <input
                      type="text"
                      value={releaseReason}
                      onChange={(e) => setReleaseReason(e.target.value)}
                      placeholder="Release reason..."
                      className="px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={() => setReleaseHoldId(null)}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => releaseHoldMutation.mutate(hold.id)}
                        disabled={releaseHoldMutation.isPending || !releaseReason.trim()}
                        className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        {releaseHoldMutation.isPending ? 'Releasing...' : 'Confirm'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setReleaseHoldId(hold.id)}
                    className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                  >
                    <ShieldOff className="h-4 w-4" />
                    Release
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Past Holds */}
      {pastHolds.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-500">Hold History</h4>
          {pastHolds.map((hold) => (
            <div
              key={hold.id}
              className="p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-start gap-3">
                <ShieldOff className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-gray-700">{hold.holdName}</p>
                  <p className="text-gray-500 text-xs mt-1">
                    {hold.status === 'Released' ? 'Released' : 'Expired'}{' '}
                    {hold.releasedAt && format(new Date(hold.releasedAt), 'MMM d, yyyy')}
                    {hold.releaseReason && ` - ${hold.releaseReason}`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && holds?.length === 0 && (
        <div className="text-center py-6 text-gray-500 text-sm">
          <Shield className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p>No legal holds on record</p>
        </div>
      )}
    </div>
  );
}

export default LegalHoldManager;
